import { buttonClass } from "@studyward/ui";
import { lazy, Suspense, type ComponentType } from "react";
import { Link, Navigate, Route, Routes, useParams } from "react-router";
import { AuthLayout } from "./components/auth";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PublicOnly, RequireAuth, RequireSetup } from "./components/guards";
import { FullPageLoading } from "./components/ui";
import { ScreenTracking } from "./lib/analytics";
import { isConfigured } from "./lib/env";

/** Wraps React.lazy with automatic recovery if a deployment replaced the requested chunk hash */
function safeLazy<T extends ComponentType<unknown>>(importer: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      return await importer();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err || "");
      const isChunkError =
        msg.includes("dynamically imported module") ||
        msg.includes("Loading chunk") ||
        msg.includes("Failed to fetch") ||
        (err instanceof Error && err.name === "TypeError");

      if (isChunkError && typeof window !== "undefined") {
        const key = "sw_chunk_reload";
        const last = sessionStorage.getItem(key);
        const now = Date.now();
        if (!last || now - Number(last) > 10_000) {
          sessionStorage.setItem(key, String(now));
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    }
  });
}

const SignIn = safeLazy(() => import("./pages/SignIn"));
const SignUp = safeLazy(() => import("./pages/SignUp"));
const AuthCallback = safeLazy(() => import("./pages/AuthCallback"));
const NewPassword = safeLazy(() => import("./pages/NewPassword"));
const Onboarding = safeLazy(() => import("./pages/Onboarding"));
const Dashboard = safeLazy(() => import("./pages/Dashboard"));
const Upload = safeLazy(() => import("./pages/Upload"));
const Sets = safeLazy(() => import("./pages/Sets"));
const SetDetail = safeLazy(() => import("./pages/SetDetail"));
const AskHoot = safeLazy(() => import("./pages/AskHoot"));
const Guide = safeLazy(() => import("./pages/Guide"));
const MindMap = safeLazy(() => import("./pages/MindMap"));
const Review = safeLazy(() => import("./pages/Review"));
const QuizSetup = safeLazy(() => import("./pages/QuizSetup"));
const Practice = safeLazy(() => import("./pages/Practice"));
const PracticeHub = safeLazy(() => import("./pages/PracticeHub"));
const SessionComplete = safeLazy(() => import("./pages/SessionComplete"));
const MockNew = safeLazy(() => import("./pages/MockNew"));
const MockRun = safeLazy(() => import("./pages/MockRun"));
const MockResults = safeLazy(() => import("./pages/MockResults"));
const Groups = safeLazy(() => import("./pages/Groups"));
const GroupBoard = safeLazy(() => import("./pages/GroupBoard"));
const GroupJoin = safeLazy(() => import("./pages/GroupJoin"));
const StudyPlan = safeLazy(() => import("./pages/StudyPlan"));
const Progress = safeLazy(() => import("./pages/Progress"));
const Billing = safeLazy(() => import("./pages/Billing"));
const Settings = safeLazy(() => import("./pages/Settings"));
const SharePreview = safeLazy(() => import("./pages/SharePreview"));
const Help = safeLazy(() => import("./pages/Help"));

/** Hoot used to live at /sets/:id/buddy; old links and bookmarks still land on the right page. */
function OldHootLink() {
  const { setId = "" } = useParams();
  return <Navigate to={`/sets/${setId}/hoot`} replace />;
}

function NotFound() {
  return (
    <AuthLayout title="Page not found">
      <p className="text-center text-text-secondary">That page doesn't exist. It may have moved.</p>
      <Link to="/dashboard" className={buttonClass()}>
        Go to your dashboard
      </Link>
    </AuthLayout>
  );
}

function NotConfigured() {
  return (
    <AuthLayout title="Almost there">
      <p className="text-center text-text-secondary">Add SUPABASE_URL and SUPABASE_ANON_KEY to the project's .env to connect this app.</p>
    </AuthLayout>
  );
}

/** Web app routes (spec §10.2 views 2–9 on the app host, §4.4), plus study: flashcards, practice and mock exams. */
export function App() {
  if (!isConfigured) return <NotConfigured />;
  return (
    <ErrorBoundary>
      <Suspense fallback={<FullPageLoading />}>
        <ScreenTracking />
        <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route element={<PublicOnly />}>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
        </Route>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/new-password" element={<NewPassword />} />
        <Route path="/shares/preview/:token" element={<SharePreview />} />
        <Route element={<RequireAuth />}>
          <Route path="/onboarding" element={<Onboarding />} />
          <Route element={<RequireSetup />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/upload" element={<Upload />} />
            <Route path="/sets" element={<Sets />} />
            <Route path="/sets/:setId" element={<SetDetail />} />
            <Route path="/sets/:setId/hoot" element={<AskHoot />} />
            <Route path="/sets/:setId/buddy" element={<OldHootLink />} />
            <Route path="/sets/:setId/guide" element={<Guide />} />
            <Route path="/sets/:setId/mind-map" element={<MindMap />} />
            <Route path="/sets/:setId/review" element={<Review />} />
            <Route path="/sets/:setId/quiz" element={<QuizSetup />} />
            <Route path="/review" element={<Review />} />
            <Route path="/practice" element={<PracticeHub />} />
            <Route path="/practice/:sessionId" element={<Practice />} />
            <Route path="/sessions/:sessionId/complete" element={<SessionComplete />} />
            <Route path="/mock-exams/new" element={<MockNew />} />
            <Route path="/mock-exams/:examId" element={<MockRun />} />
            <Route path="/mock-exams/:examId/results" element={<MockResults />} />
            <Route path="/plan" element={<StudyPlan />} />
            <Route path="/study-plan" element={<Navigate to="/plan" replace />} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/groups" element={<Groups />} />
            <Route path="/groups/join/:token" element={<GroupJoin />} />
            <Route path="/groups/:groupId" element={<GroupBoard />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/support" element={<Help />} />
            <Route path="/support/:ticketId" element={<Help />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </ErrorBoundary>
);
}
